import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {buildInteractionIdProps} from '../../../../../libs/shared/core/src/helpers/interaction/interactionHelpers';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {UnreachableError} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {CompanyModel} from '../../../../../libs/shared/core/src/models/api/company/companyModel';
import {InboxModel} from '../../../../../libs/shared/core/src/models/api/inboxModel';
import {TeammateModel} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {buildTeammateDisplayName} from '../../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserTheme} from '../../../../../libs/shared/ui/src/theme/themeTypes';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {
  CellsRefinementOptionsEnum,
  RefinementOptionModel,
} from '../../../../models/api/refinementOptionModel';
import {TagModel} from '../../../../models/api/tagModel';
import {
  InboxesParametersTypesEnum,
  RefinedInboxParameters,
} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {CellsRefinementOptionsSearchDropdownContainer} from './cellsRefinementOptionsSearchDropdownContainer';

/*
 * Constants.
 */

const largeDividerHeight = 30;

/*
 * Props.
 */

interface CellsRefinementOptionsProps {
  resultsCount: number | undefined;
  onRefinementOptionSelect: (model: RefinementOptionModel) => void;
  tagNamespaces: ReadonlyArray<string>;
  selectedRefinement: RefinementOptionModel | undefined;
  company: CompanyModel;
  parameters: RefinedInboxParameters;
  selectedAssignee: TeammateModel | undefined;
  selectedInbox: InboxModel | undefined;
  selectedTag: TagModel | undefined;
}

/*
 * Style.
 */

interface RefinementOptionStyleProps {
  $isOpen: boolean;
  $hasHover: boolean;
  $hasSelection: boolean;
}
const StyledRefinementOptionDiv = styled.div<RefinementOptionStyleProps>`
  display: flex;
  align-items: center;
  border-radius: 100px;
  padding: 2px 8px;
  overflow: hidden;
  ${(p) => addRefinementOptionStyles(p)};

  min-width: 0;
  max-width: 424px;
`;

function addRefinementOptionStyles({$isOpen, $hasHover, $hasSelection}: RefinementOptionStyleProps) {
  if ($hasSelection && $isOpen) {
    return css`
      color: ${(p) => p.theme.greys.shade90};
      background: ${(p) => p.theme.palette.blue.hover30};
    `;
  }

  if ($hasSelection) {
    return css`
      color: ${(p) => p.theme.greys.shade90};
      background: ${(p) => p.theme.palette.blue.shade30};
      &:hover {
        background: ${(p) => p.theme.palette.blue.hover30};
      }
    `;
  }

  if ($isOpen) {
    return css`
      color: ${(p) => p.theme.greys.shade80};
      background: ${(p) => p.theme.alphas.gray10};
    `;
  }

  if ($hasHover) {
    return css`
      &:hover {
        color: ${(p) => p.theme.greys.shade80};
        background: ${(p) => p.theme.alphas.gray10};
      }
    `;
  }

  return '';
}

const StyledRefinementIcon = styled(Icon)`
  margin-right: 4px;
  flex-shrink: 0;
`;

const StyledFilterSpan = styled.span`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const StyledResultsCountSpan = styled.span`
  flex-shrink: 0;
  min-width: 34px; /* Wide enough to prevent width-flicker if the count is empty */
  text-align: center;
`;

// We must use `fill:none` to Icon to avoid filling the chevron with `currentColor`.
const StyledChevronIcon = styled(Icon)`
  margin-left: 5px;
  fill: none;
  flex-shrink: 0;
`;

const StyledSelectedChevronIcon = styled(StyledChevronIcon)`
  margin-left: 0px;
`;

const StyledPopoverCoordinator = styled(PopoverCoordinator)`
  display: flex;
  align-items: center;
  height: ${largeDividerHeight}px;
  background-color: ${(p) => p.theme.alphas.transparent};
  border-bottom: 1px solid ${(p) => p.theme.alphas.transparent};
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
  ${ellipsis()};
  height: auto;
  justify-content: flex-start;
  margin: 0 0 0 14px;
`;

/*
 * Component.
 */

export const CellsRefinementOptions: FC<CellsRefinementOptionsProps> = (props) => (
  <UserThemeConsumer>
    {(theme) => (
      <StyledPopoverCoordinator
        renderButton={(isOpen) => renderTitle(props, theme, isOpen)}
        renderModal={(popoverProps) => renderDropdown(popoverProps, props)}
        onPopoverToggle={(isOpen) => trackOpenDropdown(isOpen, props.parameters)}
      />
    )}
  </UserThemeConsumer>
);

function renderTitle(props: CellsRefinementOptionsProps, theme: UserTheme, isOpen: boolean) {
  const {selectedRefinement} = props;
  return (
    <StyledRefinementOptionDiv
      $isOpen={isOpen}
      $hasHover
      $hasSelection={selectedRefinement !== undefined}
      {...buildInteractionIdProps('p2-header-refinements')}
    >
      {selectedRefinement !== undefined
        ? renderSelectedTitle(props, theme)
        : renderDefaultTitle(props, theme)}
    </StyledRefinementOptionDiv>
  );
}

function renderDefaultTitle(props_: CellsRefinementOptionsProps, theme: UserTheme) {
  return (
    <>
      <StyledRefinementIcon name="filtersSmall" />
      <Trans id="cells-refinement-options-title-unselected">Filter</Trans>
      <StyledChevronIcon name="chevronDownTiny" />
    </>
  );
}

function renderSelectedTitle(props: CellsRefinementOptionsProps, theme: UserTheme) {
  return (
    <>
      <StyledRefinementIcon name="filtersFilledSmall" color={theme.refinedP2ResultsIndicator} />
      <StyledFilterSpan>
        <Trans id="cells-refinement-options-pill-filtered">Filtered</Trans>:&nbsp;
        {renderRefinementName(props)}
      </StyledFilterSpan>
      {maybeRenderResultsCount(props)}
      <StyledSelectedChevronIcon name="chevronDownTiny" />
    </>
  );
}

function maybeRenderResultsCount(props: CellsRefinementOptionsProps) {
  const {resultsCount} = props;

  if (resultsCount === undefined) {
    return <StyledResultsCountSpan />;
  }

  if (resultsCount > 99) {
    return <StyledResultsCountSpan>(99+)</StyledResultsCountSpan>;
  }

  return <StyledResultsCountSpan>({resultsCount})</StyledResultsCountSpan>;
}

function renderRefinementName(props: CellsRefinementOptionsProps) {
  const {selectedAssignee, selectedInbox, selectedTag} = props;

  if (selectedAssignee !== undefined) {
    return <span>{buildTeammateDisplayName(selectedAssignee)}</span>;
  }

  if (selectedInbox !== undefined) {
    return <span>{selectedInbox.name}</span>;
  }

  if (selectedTag !== undefined) {
    return <span>{selectedTag.name}</span>;
  }

  // This case could potentially occur if the load tags request fails.
  return <span>&hellip;</span>;
}

function renderDropdown(popoverProps: PopoverTransferProps, props: CellsRefinementOptionsProps) {
  const {selectedRefinement, parameters, tagNamespaces, company, onRefinementOptionSelect} = props;
  return (
    <CellsRefinementOptionsSearchDropdownContainer
      popoverProps={popoverProps}
      selectedRefinement={selectedRefinement}
      company={company}
      tagNamespaces={tagNamespaces}
      parameters={parameters}
      onSelectAssignee={(teammate) =>
        onRefinementOptionSelect({
          id: teammate.id,
          type: CellsRefinementOptionsEnum.ASSIGNEE,
        })
      }
      onSelectInbox={(inbox) =>
        onRefinementOptionSelect({
          id: inbox.id,
          type: CellsRefinementOptionsEnum.INBOX,
        })
      }
      onSelectTag={(tag) =>
        onRefinementOptionSelect({
          id: tag.id,
          type: CellsRefinementOptionsEnum.TAG,
        })
      }
    />
  );
}

function trackOpenDropdown(isOpen: boolean, parameters: RefinedInboxParameters) {
  if (!isOpen) {
    return;
  }

  const workspaceType = parameters.type;

  let workspaceId: number;
  switch (parameters.type) {
    case InboxesParametersTypesEnum.TEAMMATE_INBOX:
    case InboxesParametersTypesEnum.INBOX:
      workspaceId = parameters.inboxId;
      break;
    case InboxesParametersTypesEnum.TEAM:
      workspaceId = parameters.teamId;
      break;
    case InboxesParametersTypesEnum.TEAMMATE_LENS:
    case InboxesParametersTypesEnum.TEAM_LENS:
      workspaceId = parameters.lensId;
      break;
    case InboxesParametersTypesEnum.USER:
      workspaceId = parameters.teammateId;
      break;
    default:
      throw new UnreachableError(parameters);
  }

  trackInterfaceEvent(InterfaceEventsEnum.P2_FILTER_OPEN, {
    workspace_type: workspaceType,
    workspace_id: workspaceId,
  });
}
